// ============================================================================
// SupportReplyBlock.tsx — ответ по обращению (replyTicket) + управление
// ШАБЛОНАМИ ответов (listReplyTemplates/saveReplyTemplate/deleteReplyTemplate).
// Ответ уходит в личный чат пользователя ОТ бота «Поддержка» (доставка/уведомления
// — механизм мессенджера). Доступно только специалистам поддержки (гейт сервера).
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import type { SupportReplyTemplate } from '@zdc/sdk';
import { getSDK } from '@/store/sdkStore';
import { useToastStore } from '@/store/toastStore';
import { Button } from '@/components/ui/button';
import { confirmDialog } from '@/lib/dialogs';
import { logErrorToJournal } from '@/lib/errors';
import { supportErrorText } from '@/lib/supportInbox';

interface Props {
  ticketId: string;
  /** Ответ отправлен — карточка/очередь перечитываются. */
  onReplied: () => void;
}

export function SupportReplyBlock({ ticketId, onReplied }: Props) {
  const showError = useToastStore((s) => s.showError);
  const showSuccess = useToastStore((s) => s.showSuccess);

  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [templates, setTemplates] = useState<SupportReplyTemplate[]>([]);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newBody, setNewBody] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);

  const loadTemplates = useCallback(async () => {
    try {
      const r = await getSDK().support.listReplyTemplates({});
      setTemplates(r.items);
    } catch (e) {
      logErrorToJournal(e, 'components/support/SupportReplyBlock.tsx:шаблоны');
      showError(supportErrorText(e));
    }
  }, [showError]);

  useEffect(() => {
    void loadTemplates();
  }, [loadTemplates]);

  const applyTemplate = (id: string) => {
    const tpl = templates.find((t) => t.id === id);
    if (tpl) setText(tpl.body);
  };

  const send = async () => {
    const body = text.trim();
    if (!body) {
      showError('Введите текст ответа.');
      return;
    }
    setSending(true);
    try {
      await getSDK().support.replyTicket({ ticketId, text: body });
      setText('');
      showSuccess('Ответ отправлен пользователю от «Поддержки»');
      onReplied();
    } catch (e) {
      logErrorToJournal(e, 'components/support/SupportReplyBlock.tsx:ответ');
      showError(supportErrorText(e));
    } finally {
      setSending(false);
    }
  };

  const saveTemplate = async () => {
    const title = newTitle.trim();
    const body = newBody.trim();
    if (!title || !body) {
      showError('Заполните заголовок и текст шаблона.');
      return;
    }
    setSavingTemplate(true);
    try {
      await getSDK().support.saveReplyTemplate({ id: '', title, body, sortOrder: 0 });
      setNewTitle('');
      setNewBody('');
      await loadTemplates();
      showSuccess('Шаблон сохранён');
    } catch (e) {
      logErrorToJournal(e, 'components/support/SupportReplyBlock.tsx:шаблон');
      showError(supportErrorText(e));
    } finally {
      setSavingTemplate(false);
    }
  };

  const removeTemplate = async (tpl: SupportReplyTemplate) => {
    const ok = await confirmDialog(`Шаблон «${tpl.title}» будет удалён.`, 'Удалить шаблон?');
    if (!ok) return;
    try {
      await getSDK().support.deleteReplyTemplate({ id: tpl.id });
      await loadTemplates();
      showSuccess('Шаблон удалён');
    } catch (e) {
      logErrorToJournal(e, 'components/support/SupportReplyBlock.tsx:удаление шаблона');
      showError(supportErrorText(e));
    }
  };

  const selectClass = 'h-8 min-w-0 rounded-md border bg-transparent px-2 text-sm';

  return (
    <div className="space-y-2 rounded-lg border px-3 py-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm font-medium">Ответ пользователю</span>
        <button
          type="button"
          className="text-[11px] text-muted-foreground underline-offset-2 hover:underline"
          onClick={() => setTemplatesOpen((v) => !v)}
        >
          Шаблоны ({templates.length})
        </button>
      </div>

      {templatesOpen && (
        <div className="space-y-2 rounded-md border bg-muted/20 px-2 py-2">
          {templates.length === 0 && (
            <div className="text-[11px] text-muted-foreground">Шаблонов пока нет.</div>
          )}
          {templates.map((tpl) => (
            <div key={tpl.id} className="flex items-center justify-between gap-2">
              <span className="min-w-0 flex-1 truncate text-[12px]" title={tpl.body}>
                {tpl.title}
              </span>
              <Button
                size="sm"
                variant="ghost"
                className="h-6 text-[11px]"
                onClick={() => setText(tpl.body)}
              >
                Вставить
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-6 text-[11px] text-destructive"
                onClick={() => void removeTemplate(tpl)}
              >
                Удалить
              </Button>
            </div>
          ))}
          <div className="space-y-1 border-t pt-2">
            <input
              className="h-8 w-full rounded-md border bg-transparent px-2 text-sm"
              placeholder="Заголовок нового шаблона"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
            />
            <textarea
              className="h-16 w-full rounded-md border bg-transparent px-2 py-1 text-sm"
              placeholder="Текст шаблона"
              value={newBody}
              onChange={(e) => setNewBody(e.target.value)}
            />
            <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => void saveTemplate()} disabled={savingTemplate}>
              {savingTemplate ? 'Сохраняем…' : 'Добавить шаблон'}
            </Button>
          </div>
        </div>
      )}

      {templates.length > 0 && (
        <select className={selectClass} value="" onChange={(e) => applyTemplate(e.target.value)}>
          <option value="">Шаблон ответа…</option>
          {templates.map((tpl) => (
            <option key={tpl.id} value={tpl.id}>{tpl.title}</option>
          ))}
        </select>
      )}

      <textarea
        className="h-24 w-full rounded-md border bg-transparent px-2 py-1 text-sm"
        placeholder="Текст ответа — уйдёт пользователю от бота «Поддержка»"
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      <div className="flex justify-end">
        <Button size="sm" className="h-7 text-[11px]" onClick={() => void send()} disabled={sending}>
          {sending ? 'Отправляем…' : 'Отправить ответ'}
        </Button>
      </div>
    </div>
  );
}
